import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AtriumPanel from "@/components/atrium/AtriumPanel";
import AtriumIcon from "@/components/atrium/AtriumIcon";
import { useVaultArticles, type VaultArticle } from "@/hooks/useVaultArticles";
import { KNOWLEDGE_LINKS } from "@/components/atrium/atriumData";

const VALID_KINDS: VaultArticle["kind"][] = ["reference", "meeting", "report", "decision", "insight", "person", "project"];

function coerceKind(kind: string | null): VaultArticle["kind"] | undefined {
  return kind && VALID_KINDS.includes(kind as VaultArticle["kind"])
    ? kind as VaultArticle["kind"]
    : undefined;
}

export default function AtriumKnowledge() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("query") ?? "");
  const kind = coerceKind(params.get("kind"));
  const { data, isLoading } = useVaultArticles({
    query: q || undefined,
    kind,
    limit: 30,
  });
  const total = data?.length ?? 0;

  useEffect(() => {
    const next = new URLSearchParams(params);
    if (q) next.set("query", q);
    else next.delete("query");
    setParams(next, { replace: true });
    // params intentionally omitted: this syncs local input to URL without
    // resetting other route-driven filters such as kind.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, setParams]);

  return (
    <div className="p-5 flex flex-col gap-4">
      <div>
        <div className="text-[24px] font-semibold tracking-[-0.02em]">Knowledge</div>
        <div className="text-[13px] mt-0.5" style={{ color: "var(--a-muted)" }}>
          Vault cache search and recent articles from Agency Supabase.
        </div>
      </div>

      <div className="a-card px-[14px] py-2.5 flex items-center gap-2.5">
        <span style={{ color: "var(--a-muted)" }}>⌕</span>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="search the vault..."
          className="flex-1 bg-transparent border-0 text-[13px] outline-none"
          style={{ color: "var(--a-text)" }}
        />
        {kind && <span className="a-chip">{kind}</span>}
        <span className="text-[11px]" style={{ color: "var(--a-faint)" }}>{total}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AtriumPanel title="Recent">
          {isLoading && (
            <div className="px-[14px] py-2.5 text-[12px]" style={{ color: "var(--a-muted)" }}>Loading...</div>
          )}
          {!isLoading && total === 0 && (
            <div className="px-[14px] py-2.5 text-[12px]" style={{ color: "var(--a-muted)" }}>No matches.</div>
          )}
          {(data ?? []).map((k) => (
            <div key={k.slug} className="flex items-center gap-2.5 px-[14px] py-2.5 text-[13px]">
              <AtriumIcon name="book" size={13} />
              <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">{k.title}</span>
              <span className="a-chip">{k.kind}</span>
              <span className="a-chip">{k.client}</span>
              <span className="mono text-[11px]" style={{ color: "var(--a-faint)" }}>
                {new Date(k.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </span>
            </div>
          ))}
        </AtriumPanel>

        <AtriumPanel title="Filters">
          {KNOWLEDGE_LINKS.map((l) => (
            <Link key={l.label} to={l.to} className="flex items-center gap-2.5 px-[14px] py-2.5 text-[13px] cursor-pointer a-link a-row-hover">
              <AtriumIcon name="book" size={13} />
              <span className="flex-1">{l.label}</span>
              <AtriumIcon name="arrow" size={11} />
            </Link>
          ))}
        </AtriumPanel>
      </div>
    </div>
  );
}
